import React, { useState, useEffect, useMemo } from "react";
import {
  TicketPercent,
  Plus,
  Search,
  Copy,
  Check,
  Calendar,
  Sparkles,
  RefreshCw,
  Tag,
  ShoppingBag,
  IndianRupee,
  Percent,
  CheckCircle2,
  AlertCircle,
  Clock,
  Layers,
  Edit2,
  Trash2,
  Power,
  X,
  Flame,
  Eye,
  ArrowRight,
  Megaphone,
} from "lucide-react";

const getApiBase = () => {
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return "http://localhost:5000";
  }
  return (
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "https://api.vanyacollectionsrajahmundry.com"
  );
};
const API_BASE = getApiBase();

const getTodayStr = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const addDaysStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const AddCouponForm = () => {
  // Navigation Tab: 'coupons' (Default) | 'sale_popup' (Sale of Day Popup)
  const [activeTab, setActiveTab] = useState("coupons");

  // =========================================================================
  // STANDARD COUPONS STATE
  // =========================================================================
  const [form, setForm] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    apply_type: "cart",
    category_name: "",
    product_id: "",
    min_amount: "",
    expiry_date: "",
    is_active: true,
  });

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [copiedCode, setCopiedCode] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // =========================================================================
  // SALE OF THE DAY / PROMO POPUP STATE
  // =========================================================================
  const initialSaleForm = {
    title: "Sale of the Day!",
    tagline: "Exclusive 24-Hour Deal",
    description: "Enjoy special discounts on our exquisite handwoven saree collection. Limited time royal offer!",
    discount_type: "percentage",
    discount_value: "50",
    offer_badge: "FLAT 50% OFF",
    apply_type: "all", // 'all' | 'category' | 'amount'
    category_name: "",
    min_amount: "",
    coupon_code: "SALE50",
    start_date: getTodayStr(),
    end_date: getTodayStr(),
    cta_text: "Shop the Sale Now",
    cta_link: "/shop",
    is_active: true,
  };

  const [saleForm, setSaleForm] = useState(initialSaleForm);
  const [salePopups, setSalePopups] = useState([]);
  const [saleLoading, setSaleLoading] = useState(false);
  const [saleSubmitting, setSaleSubmitting] = useState(false);
  const [editingSaleId, setEditingSaleId] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [saleSearchTerm, setSaleSearchTerm] = useState("");

  // --------------------------------------------------
  // LOAD DATA ON MOUNT
  // --------------------------------------------------
  useEffect(() => {
    fetchCoupons();
    fetchCategories();
    fetchSalePopups();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/cart/coupons/all`);
      const data = await res.json();
      if (res.ok && data.success) {
        setCoupons(Array.isArray(data.coupons) ? data.coupons : []);
      }
    } catch (err) {
      console.error("Error fetching coupons:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/categories`);
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.categories || [];
        setCategories(list);
      }
    } catch (err) {
      console.warn("Could not load categories list", err);
    }
  };

  const fetchSalePopups = async () => {
    try {
      setSaleLoading(true);
      const res = await fetch(`${API_BASE}/cart/sale-popup/all`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSalePopups(Array.isArray(data.popups) ? data.popups : []);
        }
      }
    } catch (err) {
      console.error("Error fetching sale popups:", err);
    } finally {
      setSaleLoading(false);
    }
  };

  // --------------------------------------------------
  // SALE POPUP HELPERS & COMPUTED
  // --------------------------------------------------
  const isSaleCurrentlyLive = (p) => {
    if (!p || !p.is_active) return false;
    const now = new Date();
    if (p.start_date) {
      const s = new Date(p.start_date);
      s.setHours(0, 0, 0, 0);
      if (s > now) return false;
    }
    if (p.end_date) {
      const e = new Date(p.end_date);
      e.setHours(23, 59, 59, 999);
      if (e < now) return false;
    }
    return true;
  };

  const currentLiveSale = useMemo(() => {
    return salePopups.find((p) => isSaleCurrentlyLive(p));
  }, [salePopups]);

  const getSaleStatusInfo = (p) => {
    if (!p.is_active) {
      return {
        label: "Stopped / Disabled",
        color: "#64748b",
        bg: "#f1f5f9",
        border: "#cbd5e1",
      };
    }
    const now = new Date();
    if (p.end_date) {
      const e = new Date(p.end_date);
      e.setHours(23, 59, 59, 999);
      if (e < now) {
        return {
          label: "Expired (Auto-Stopped)",
          color: "#dc2626",
          bg: "#fef2f2",
          border: "#fecaca",
        };
      }
    }
    if (p.start_date) {
      const s = new Date(p.start_date);
      s.setHours(0, 0, 0, 0);
      if (s > now) {
        return {
          label: "Scheduled",
          color: "#2563eb",
          bg: "#eff6ff",
          border: "#bfdbfe",
        };
      }
    }
    return {
      label: "LIVE on Website",
      color: "#059669",
      bg: "#ecfdf5",
      border: "#a7f3d0",
      isLive: true,
    };
  };

  // Quick Date presets
  const handleSetSalePreset = (preset) => {
    const today = getTodayStr();
    if (preset === "today") {
      setSaleForm((prev) => ({
        ...prev,
        start_date: today,
        end_date: today,
      }));
    } else if (preset === "3days") {
      setSaleForm((prev) => ({
        ...prev,
        start_date: today,
        end_date: addDaysStr(2),
      }));
    } else if (preset === "weekend") {
      setSaleForm((prev) => ({
        ...prev,
        start_date: today,
        end_date: addDaysStr(3),
      }));
    } else if (preset === "7days") {
      setSaleForm((prev) => ({
        ...prev,
        start_date: today,
        end_date: addDaysStr(6),
      }));
    } else if (preset === "30days") {
      setSaleForm((prev) => ({
        ...prev,
        start_date: today,
        end_date: addDaysStr(29),
      }));
    }
  };

  // Form Change for Sale Popup
  const handleSaleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSaleForm((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // Auto update offer badge if user is changing discount_value or discount_type
      if (name === "discount_value" || name === "discount_type") {
        const dType = name === "discount_type" ? value : prev.discount_type;
        const dVal = name === "discount_value" ? value : prev.discount_value;
        if (dVal && Number(dVal) > 0) {
          updated.offer_badge =
            dType === "percentage"
              ? `FLAT ${dVal}% OFF`
              : `FLAT ₹${Number(dVal).toLocaleString("en-IN")} OFF`;
        }
      }

      return updated;
    });
  };

  // Save / Update Sale Popup
  const handleSaveSalePopup = async (e) => {
    e.preventDefault();
    setFeedback(null);

    if (!saleForm.title.trim()) {
      setFeedback({ type: "error", message: "Please enter a Sale Title / Heading." });
      return;
    }
    if (!saleForm.start_date || !saleForm.end_date) {
      setFeedback({ type: "error", message: "Please specify both From Date and To Date." });
      return;
    }
    if (new Date(saleForm.end_date) < new Date(saleForm.start_date)) {
      setFeedback({ type: "error", message: "End Date cannot be earlier than Start Date." });
      return;
    }

    try {
      setSaleSubmitting(true);
      const url = editingSaleId
        ? `${API_BASE}/cart/sale-popup/${editingSaleId}`
        : `${API_BASE}/cart/sale-popup`;
      const method = editingSaleId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(saleForm),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to save sale popup");
      }

      setFeedback({
        type: "success",
        message: editingSaleId
          ? `Sale Popup "${saleForm.title}" updated successfully!`
          : `Sale Popup "${saleForm.title}" created successfully! It is now configured for your website.`,
      });

      // Reset
      setSaleForm(initialSaleForm);
      setEditingSaleId(null);
      await fetchSalePopups();
      setTimeout(() => setFeedback(null), 5000);
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", message: err.message || "Error saving sale popup" });
    } finally {
      setSaleSubmitting(false);
    }
  };

  // Toggle Live / Stopped Status
  const handleToggleSalePopup = async (p) => {
    const nextStatus = !p.is_active;
    try {
      setActionLoadingId(p.id);
      const res = await fetch(`${API_BASE}/cart/sale-popup/${p.id}/toggle`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: nextStatus }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update status");
      }

      setSalePopups((prev) =>
        prev.map((item) => (item.id === p.id ? { ...item, is_active: nextStatus } : item))
      );

      setFeedback({
        type: "success",
        message: nextStatus
          ? `Sale "${p.title}" is now LIVE on the customer website!`
          : `Sale "${p.title}" has been STOPPED and will not show on the website.`,
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", message: err.message || "Failed to toggle status" });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Delete Sale Popup
  const handleDeleteSalePopup = async (p) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete sale popup "${p.title}"?\n\nIt will be completely removed.`
    );
    if (!confirmDelete) return;

    try {
      setActionLoadingId(p.id);
      const res = await fetch(`${API_BASE}/cart/sale-popup/${p.id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to delete sale popup");
      }

      setSalePopups((prev) => prev.filter((item) => item.id !== p.id));
      if (editingSaleId === p.id) {
        setEditingSaleId(null);
        setSaleForm(initialSaleForm);
      }

      setFeedback({
        type: "success",
        message: `Sale popup "${p.title}" deleted.`,
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", message: err.message || "Failed to delete sale popup" });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Edit Sale Popup
  const handleStartEditSale = (p) => {
    setEditingSaleId(p.id);
    setSaleForm({
      title: p.title || "",
      tagline: p.tagline || "",
      description: p.description || "",
      discount_type: p.discount_type || "percentage",
      discount_value: p.discount_value || "",
      offer_badge: p.offer_badge || "",
      apply_type: p.apply_type || "all",
      category_name: p.category_name || "",
      min_amount: p.min_amount || "",
      coupon_code: p.coupon_code || "",
      start_date: p.start_date ? String(p.start_date).split("T")[0] : getTodayStr(),
      end_date: p.end_date ? String(p.end_date).split("T")[0] : getTodayStr(),
      cta_text: p.cta_text || "Shop the Sale Now",
      cta_link: p.cta_link || "/shop",
      is_active: p.is_active !== undefined ? Boolean(p.is_active) : true,
    });
    window.scrollTo({ top: 380, behavior: "smooth" });
  };

  // Open Live Preview Modal
  const handleOpenPreview = (data) => {
    setPreviewData(data);
    setPreviewModalOpen(true);
  };

  // =========================================================================
  // STANDARD COUPONS FORM HANDLERS
  // =========================================================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "code"
          ? value.toUpperCase().replace(/\s+/g, "")
          : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    if (!form.code.trim()) {
      setFeedback({ type: "error", message: "Please enter a valid coupon code." });
      return;
    }
    if (!form.discount_value || Number(form.discount_value) <= 0) {
      setFeedback({ type: "error", message: "Discount value must be greater than zero." });
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE}/cart/add/coupons`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Error adding coupon");
      }

      setFeedback({
        type: "success",
        message: `Coupon "${data.coupon?.code || form.code}" created successfully!`,
      });

      setForm({
        code: "",
        discount_type: "percentage",
        discount_value: "",
        apply_type: "cart",
        category_name: "",
        product_id: "",
        min_amount: "",
        expiry_date: "",
        is_active: true,
      });

      await fetchCoupons();
      setTimeout(() => setFeedback(null), 5000);
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", message: err.message || "Failed to create coupon" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleToggleStatus = async (coupon) => {
    const nextStatus = !coupon.is_active;
    try {
      setActionLoadingId(coupon.id);
      const res = await fetch(`${API_BASE}/cart/coupons/${coupon.id}/toggle`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: nextStatus }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update status");
      }

      setCoupons((prev) =>
        prev.map((c) => (c.id === coupon.id ? { ...c, is_active: nextStatus } : c))
      );

      setFeedback({
        type: "success",
        message: `Coupon "${coupon.code}" is now ${nextStatus ? "Enabled" : "Disabled"}.`,
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", message: err.message || "Failed to update status" });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteCoupon = async (coupon) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete coupon "${coupon.code}"?`
    );
    if (!confirmDelete) return;

    try {
      setActionLoadingId(coupon.id);
      const res = await fetch(`${API_BASE}/cart/coupons/${coupon.id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to delete coupon");
      }

      setCoupons((prev) => prev.filter((c) => c.id !== coupon.id));

      setFeedback({
        type: "success",
        message: `Coupon "${coupon.code}" permanently deleted.`,
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", message: err.message || "Failed to delete coupon" });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleStartEdit = (coupon) => {
    setEditingCoupon(coupon);
    setEditForm({
      id: coupon.id,
      code: coupon.code || "",
      discount_type: coupon.discount_type || "percentage",
      discount_value: coupon.discount_value || "",
      apply_type: coupon.apply_type || "cart",
      category_name: coupon.category_name || "",
      product_id: coupon.product_id || "",
      min_amount: coupon.min_amount || "",
      expiry_date: coupon.expiry_date ? String(coupon.expiry_date).split("T")[0] : "",
      is_active: coupon.is_active !== undefined ? Boolean(coupon.is_active) : true,
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "code"
          ? value.toUpperCase().replace(/\s+/g, "")
          : value,
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.code.trim()) {
      alert("Coupon code is required");
      return;
    }
    if (!editForm.discount_value || Number(editForm.discount_value) <= 0) {
      alert("Discount value must be greater than zero");
      return;
    }

    try {
      setEditSubmitting(true);
      const res = await fetch(`${API_BASE}/cart/coupons/${editForm.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update coupon");
      }

      setCoupons((prev) =>
        prev.map((c) => (c.id === editForm.id ? { ...c, ...data.coupon } : c))
      );
      setEditingCoupon(null);
      setEditForm(null);

      setFeedback({
        type: "success",
        message: `Coupon "${editForm.code}" updated successfully!`,
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to update coupon");
    } finally {
      setEditSubmitting(false);
    }
  };

  // Helper date
  const formatDate = (date) => {
    if (!date) return "-";
    try {
      const d = new Date(date);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return String(date).split("T")[0];
    }
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const now = new Date();
    const exp = new Date(expiryDate);
    exp.setHours(23, 59, 59, 999);
    return exp < now;
  };

  const setExpiryDays = (days) => {
    setForm((prev) => ({ ...prev, expiry_date: addDaysStr(days) }));
  };

  const setEditExpiryDays = (days) => {
    setEditForm((prev) => ({ ...prev, expiry_date: addDaysStr(days) }));
  };

  // Filtered coupons
  const filteredCoupons = useMemo(() => {
    return coupons.filter((c) => {
      const matchesSearch =
        c.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.apply_type?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;
      if (statusFilter === "active") return c.is_active && !isExpired(c.expiry_date);
      if (statusFilter === "disabled") return !c.is_active;
      if (statusFilter === "expired") return isExpired(c.expiry_date);
      return true;
    });
  }, [coupons, searchTerm, statusFilter]);

  const metrics = useMemo(() => {
    const total = coupons.length;
    const active = coupons.filter((c) => c.is_active && !isExpired(c.expiry_date)).length;
    const disabled = coupons.filter((c) => !c.is_active).length;
    const expired = coupons.filter((c) => isExpired(c.expiry_date)).length;
    return { total, active, disabled, expired };
  }, [coupons]);

  // Filtered Sale Popups
  const filteredSalePopups = useMemo(() => {
    if (!saleSearchTerm.trim()) return salePopups;
    const q = saleSearchTerm.toLowerCase();
    return salePopups.filter(
      (p) =>
        p.title?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.category_name?.toLowerCase().includes(q) ||
        p.coupon_code?.toLowerCase().includes(q)
    );
  }, [salePopups, saleSearchTerm]);

  return (
    <div style={styles.pageContainer}>
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}
      <div style={styles.pageHeader}>
        <div>
          <div style={styles.badgeLabel}>
            <Sparkles size={14} color="#D4AF37" />
            <span>DISCOUNTS, OFFERS & PROMOTIONAL ENGINE</span>
          </div>
          <h1 style={styles.title}>Promotions & Offers Center</h1>
          <p style={styles.subtitle}>
            Control website-wide “Sale of the Day” promotional popups for all visitors and manage customer coupon discount codes.
          </p>
        </div>

        <button
          onClick={() => {
            fetchCoupons();
            fetchSalePopups();
          }}
          disabled={loading || saleLoading}
          style={styles.refreshBtn}
          title="Refresh data"
        >
          <RefreshCw size={16} className={loading || saleLoading ? "spin-icon" : ""} />
          <span>{loading || saleLoading ? "Refreshing..." : "Refresh All"}</span>
        </button>
      </div>

      {/* =====================================================
          MAIN TAB SWITCHER (COUPONS vs SALE FOR THE DAY)
      ===================================================== */}
      <div style={styles.tabNavContainer}>
        <button
          type="button"
          onClick={() => setActiveTab("coupons")}
          style={{
            ...styles.mainTabBtn,
            ...(activeTab === "coupons" ? styles.mainTabBtnActive : {}),
          }}
        >
          <TicketPercent size={18} color={activeTab === "coupons" ? "#D4AF37" : "#059669"} />
          <span style={{ fontWeight: "700" }}>Coupons & Voucher Codes</span>
          <span style={styles.tabCountBadge}>{coupons.length}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("sale_popup")}
          style={{
            ...styles.mainTabBtn,
            ...(activeTab === "sale_popup" ? styles.mainTabBtnActive : {}),
          }}
        >
          <Flame size={18} color={activeTab === "sale_popup" ? "#D4AF37" : "#e11d48"} />
          <span style={{ fontWeight: "700" }}>Sale of the Day (Website Popup)</span>
          {currentLiveSale ? (
            <span style={styles.livePulsePill}>
              <span style={styles.pulseDot} />
              LIVE NOW
            </span>
          ) : (
            <span style={styles.tabCountBadge}>{salePopups.length}</span>
          )}
        </button>
      </div>

      {/* FEEDBACK ALERT */}
      {feedback && (
        <div
          style={{
            ...styles.feedbackAlert,
            backgroundColor: feedback.type === "success" ? "#ecfdf5" : "#fef2f2",
            borderColor: feedback.type === "success" ? "#a7f3d0" : "#fecaca",
            color: feedback.type === "success" ? "#065f46" : "#991b1b",
          }}
        >
          {feedback.type === "success" ? (
            <CheckCircle2 size={20} color="#059669" />
          ) : (
            <AlertCircle size={20} color="#dc2626" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* =========================================================================
          TAB 1: SALE OF THE DAY (WEBSITE POPUP)
      ========================================================================= */}
      {activeTab === "sale_popup" && (
        <div>
          {/* CURRENT LIVE STATUS HERO BANNER */}
          <div
            style={{
              ...styles.liveStatusBanner,
              borderColor: currentLiveSale ? "#10b981" : "#e2e8f0",
              background: currentLiveSale
                ? "linear-gradient(135deg, rgba(20, 53, 43, 0.96) 0%, rgba(31, 74, 60, 0.98) 100%)"
                : "#ffffff",
              color: currentLiveSale ? "#ffffff" : "#1e293b",
            }}
          >
            <div style={styles.liveBannerLeft}>
              <div
                style={{
                  ...styles.liveBannerIcon,
                  backgroundColor: currentLiveSale ? "rgba(212, 175, 55, 0.2)" : "#f1f5f9",
                  color: currentLiveSale ? "#D4AF37" : "#64748b",
                }}
              >
                <Flame size={28} />
              </div>

              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <span
                    style={{
                      ...styles.liveStatusBadge,
                      backgroundColor: currentLiveSale ? "#10b981" : "#f1f5f9",
                      color: currentLiveSale ? "#ffffff" : "#64748b",
                    }}
                  >
                    {currentLiveSale ? (
                      <>
                        <span style={styles.pulseDotWhite} />
                        CURRENTLY LIVE ON WEBSITE
                      </>
                    ) : (
                      "NO SALE POPUP CURRENTLY LIVE"
                    )}
                  </span>

                  {currentLiveSale && (
                    <span style={{ fontSize: "12px", color: "#f3e5ab" }}>
                      Valid: {formatDate(currentLiveSale.start_date)} to {formatDate(currentLiveSale.end_date)}
                    </span>
                  )}
                </div>

                {currentLiveSale ? (
                  <>
                    <h2 style={{ ...styles.liveBannerTitle, color: "#ffffff" }}>
                      {currentLiveSale.title}
                      {currentLiveSale.offer_badge && (
                        <span style={styles.liveBannerOfferBadge}>
                          {currentLiveSale.offer_badge}
                        </span>
                      )}
                    </h2>
                    <p style={{ ...styles.liveBannerDesc, color: "#cbd5e1" }}>
                      {currentLiveSale.description || "Website visitors see this royal pop-up alert upon loading."}
                    </p>
                    <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", fontSize: "12.5px", color: "#f3e5ab", marginTop: "8px" }}>
                      <span>
                        <strong>Scope:</strong>{" "}
                        {currentLiveSale.apply_type === "category"
                          ? `Special category: ${currentLiveSale.category_name}`
                          : currentLiveSale.apply_type === "amount"
                          ? `Orders above ₹${Number(currentLiveSale.min_amount).toLocaleString("en-IN")}`
                          : "All Saree Collections"}
                      </span>
                      {currentLiveSale.coupon_code && (
                        <span>
                          <strong>Promo Code:</strong>{" "}
                          <code style={{ background: "rgba(0,0,0,0.3)", padding: "2px 6px", borderRadius: "4px" }}>
                            {currentLiveSale.coupon_code}
                          </code>
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 style={{ ...styles.liveBannerTitle, color: "#14352b" }}>
                      No Active Sale Popup for Visitors
                    </h2>
                    <p style={{ ...styles.liveBannerDesc, color: "#64748b" }}>
                      Configure a "Sale of the Day" below with your offer (e.g. 50% off on all sarees or specific categories). When visitors open your website, an elegant royal themed popup with animations will appear automatically!
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Emergency Action Buttons */}
            <div style={styles.liveBannerActions}>
              {currentLiveSale && (
                <>
                  <button
                    type="button"
                    onClick={() => handleToggleSalePopup(currentLiveSale)}
                    disabled={actionLoadingId === currentLiveSale.id}
                    style={styles.stopSaleBtn}
                    title="Instantly deactivate popup so customers no longer see it"
                  >
                    <Power size={16} />
                    <span>STOP SALE NOW</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOpenPreview(currentLiveSale)}
                    style={styles.previewLiveBtn}
                  >
                    <Eye size={16} />
                    <span>Preview on Website</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* =====================================================
              CREATE / EDIT SALE OF THE DAY FORM
          ===================================================== */}
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <div style={{ ...styles.cardHeaderIcon, backgroundColor: "#582b6b" }}>
                <Megaphone size={20} color="#D4AF37" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h2 style={styles.cardTitle}>
                    {editingSaleId ? `Edit Sale of the Day (#${editingSaleId})` : "Configure Sale of the Day Popup"}
                  </h2>
                  {editingSaleId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingSaleId(null);
                        setSaleForm(initialSaleForm);
                      }}
                      style={styles.cancelEditBtn}
                    >
                      <X size={14} />
                      Cancel Editing
                    </button>
                  )}
                </div>
                <p style={styles.cardSubtitle}>
                  Provide promotional headline, offer details (e.g. 50% OFF), category/amount rules, and adjust validity period from date to date.
                </p>
              </div>
            </div>

            <form onSubmit={handleSaveSalePopup} style={styles.formGrid}>
              {/* Row 1: Title & Tagline */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Sale Headline / Title</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  <Sparkles size={17} style={styles.inputIcon} />
                  <input
                    type="text"
                    name="title"
                    value={saleForm.title}
                    onChange={handleSaleChange}
                    placeholder="e.g. Sale of the Day!, Festive Dhamaka, Flash 24H Sale"
                    required
                    style={styles.input}
                  />
                </div>
                <span style={styles.helperText}>Main bold header shown at top of popup</span>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Tagline / Subtitle</span>
                </label>
                <div style={styles.inputWrapper}>
                  <Tag size={17} style={styles.inputIcon} />
                  <input
                    type="text"
                    name="tagline"
                    value={saleForm.tagline}
                    onChange={handleSaleChange}
                    placeholder="e.g. Exclusive 24-Hour Deal, Limited Period Offer"
                    style={styles.input}
                  />
                </div>
                <span style={styles.helperText}>Sub-heading directly below title</span>
              </div>

              {/* Row 2: Discount Type & Value */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Offer Discount Type</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.typeSelectorGroup}>
                  <button
                    type="button"
                    onClick={() =>
                      handleSaleChange({ target: { name: "discount_type", value: "percentage" } })
                    }
                    style={{
                      ...styles.typeSelectorBtn,
                      ...(saleForm.discount_type === "percentage" ? styles.typeSelectorBtnActive : {}),
                    }}
                  >
                    <Percent size={16} />
                    <span>Percentage (%)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleSaleChange({ target: { name: "discount_type", value: "fixed" } })
                    }
                    style={{
                      ...styles.typeSelectorBtn,
                      ...(saleForm.discount_type === "fixed" ? styles.typeSelectorBtnActive : {}),
                    }}
                  >
                    <IndianRupee size={16} />
                    <span>Fixed Amount (₹)</span>
                  </button>
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Discount Value</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  {saleForm.discount_type === "percentage" ? (
                    <Percent size={17} style={styles.inputIcon} />
                  ) : (
                    <IndianRupee size={17} style={styles.inputIcon} />
                  )}
                  <input
                    type="number"
                    step="any"
                    min="0"
                    max={saleForm.discount_type === "percentage" ? "100" : undefined}
                    name="discount_value"
                    value={saleForm.discount_value}
                    onChange={handleSaleChange}
                    placeholder={saleForm.discount_type === "percentage" ? "e.g. 50 (for 50% off)" : "e.g. 500 (for ₹500 off)"}
                    required
                    style={styles.input}
                  />
                </div>
                <span style={styles.helperText}>Numeric discount magnitude</span>
              </div>

              {/* Row 3: Offer Badge & Apply Scope */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Prominent Badge Text</span>
                </label>
                <div style={styles.inputWrapper}>
                  <Flame size={17} style={styles.inputIcon} />
                  <input
                    type="text"
                    name="offer_badge"
                    value={saleForm.offer_badge}
                    onChange={handleSaleChange}
                    placeholder="e.g. FLAT 50% OFF, ₹500 FLAT OFF"
                    style={{ ...styles.input, fontWeight: "700", color: "#14352b" }}
                  />
                </div>
                <span style={styles.helperText}>Golden pill displayed prominently in the popup</span>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Offer Scope / Target</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  <ShoppingBag size={17} style={styles.inputIcon} />
                  <select
                    name="apply_type"
                    value={saleForm.apply_type}
                    onChange={handleSaleChange}
                    style={styles.select}
                  >
                    <option value="all">All Categories (Storewide)</option>
                    <option value="category">Particular Saree Category</option>
                    <option value="amount">Minimum Order Purchase Amount</option>
                  </select>
                </div>
                <span style={styles.helperText}>Scope where this promotion applies</span>
              </div>

              {/* Conditional: Category Name */}
              {saleForm.apply_type === "category" && (
                <div style={{ ...styles.fieldGroup, ...styles.conditionalField }}>
                  <label style={styles.label}>
                    <span>Select Saree Category</span>
                    <span style={styles.requiredStar}>*</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <Layers size={17} style={styles.inputIcon} />
                    <input
                      type="text"
                      name="category_name"
                      value={saleForm.category_name}
                      onChange={handleSaleChange}
                      list="sale-category-suggestions"
                      placeholder="e.g. Silk Sarees, Handloom Sarees, Pattu Sarees"
                      required
                      style={styles.input}
                    />
                    <datalist id="sale-category-suggestions">
                      {categories.map((c, i) => (
                        <option key={c.id || i} value={c.category} />
                      ))}
                    </datalist>
                  </div>
                  <span style={styles.helperText}>Popup will state: "Special Offer on [Category] Sarees"</span>
                </div>
              )}

              {/* Conditional: Minimum Purchase Amount */}
              {saleForm.apply_type === "amount" && (
                <div style={{ ...styles.fieldGroup, ...styles.conditionalField }}>
                  <label style={styles.label}>
                    <span>Minimum Purchase Amount (₹)</span>
                    <span style={styles.requiredStar}>*</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <IndianRupee size={17} style={styles.inputIcon} />
                    <input
                      type="number"
                      step="any"
                      min="0"
                      name="min_amount"
                      value={saleForm.min_amount}
                      onChange={handleSaleChange}
                      placeholder="e.g. 2000"
                      required
                      style={styles.input}
                    />
                  </div>
                  <span style={styles.helperText}>Applicable when customer cart reaches this value</span>
                </div>
              )}

              {/* Row 4: Coupon Code & CTA Link */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Linked Coupon / Promo Code</span>
                  <small style={{ color: "#64748b", fontWeight: "normal" }}>(Optional)</small>
                </label>
                <div style={styles.inputWrapper}>
                  <Tag size={17} style={styles.inputIcon} />
                  <input
                    type="text"
                    name="coupon_code"
                    value={saleForm.coupon_code}
                    onChange={(e) =>
                      setSaleForm((p) => ({
                        ...p,
                        coupon_code: e.target.value.toUpperCase().replace(/\s+/g, ""),
                      }))
                    }
                    placeholder="e.g. SALE50, FESTIVE50"
                    style={{ ...styles.input, textTransform: "uppercase", fontWeight: "700" }}
                  />
                </div>
                <span style={styles.helperText}>Customers can tap "COPY CODE" inside the popup</span>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Button Text & Destination</span>
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  <input
                    type="text"
                    name="cta_text"
                    value={saleForm.cta_text}
                    onChange={handleSaleChange}
                    placeholder="Button text (e.g. Shop Now)"
                    style={styles.inputNoIcon}
                  />
                  <input
                    type="text"
                    name="cta_link"
                    value={saleForm.cta_link}
                    onChange={handleSaleChange}
                    placeholder="Route (e.g. /shop)"
                    style={styles.inputNoIcon}
                  />
                </div>
                <span style={styles.helperText}>Where customer lands when clicking the action button</span>
              </div>

              {/* Row 5: Promotional Matter / Description (Full width) */}
              <div style={{ ...styles.fieldGroup, ...styles.fullWidthField }}>
                <label style={styles.label}>
                  <span>Promotional Matter / Description</span>
                </label>
                <textarea
                  name="description"
                  rows={3}
                  value={saleForm.description}
                  onChange={handleSaleChange}
                  placeholder="e.g. Celebrate with timeless beauty! Enjoy flat 50% discount across our exclusive handloom weaves. Hurry, deal valid while stocks last!"
                  style={styles.textarea}
                />
                <span style={styles.helperText}>Compelling message presented in the body of the royal popup</span>
              </div>

              {/* Row 6: Validity Period Adjustment (From Date to To Date) */}
              <div style={{ ...styles.fieldGroup, ...styles.fullWidthField }}>
                <div style={styles.periodAdjustmentCard}>
                  <div style={styles.periodHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Calendar size={18} color="#D4AF37" />
                      <strong style={{ fontSize: "14px", color: "#14352b" }}>
                        Validity Period (Date Adjustment)
                      </strong>
                    </div>

                    {/* Quick Preset Buttons */}
                    <div style={styles.presetButtonsRow}>
                      <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>Quick Presets:</span>
                      <button
                        type="button"
                        onClick={() => handleSetSalePreset("today")}
                        style={styles.presetChip}
                      >
                        Today Only (24h)
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSetSalePreset("3days")}
                        style={styles.presetChip}
                      >
                        3 Days
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSetSalePreset("weekend")}
                        style={styles.presetChip}
                      >
                        Weekend Deal
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSetSalePreset("7days")}
                        style={styles.presetChip}
                      >
                        7 Days
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSetSalePreset("30days")}
                        style={styles.presetChip}
                      >
                        30 Days
                      </button>
                    </div>
                  </div>

                  <div style={styles.periodInputsRow}>
                    <div style={styles.dateField}>
                      <label style={styles.subLabel}>From Date (Start)</label>
                      <div style={styles.inputWrapper}>
                        <Calendar size={16} style={styles.inputIcon} />
                        <input
                          type="date"
                          name="start_date"
                          value={saleForm.start_date}
                          onChange={handleSaleChange}
                          required
                          style={styles.input}
                        />
                      </div>
                    </div>

                    <div style={styles.dateField}>
                      <label style={styles.subLabel}>To Date (End / Auto-Stop)</label>
                      <div style={styles.inputWrapper}>
                        <Clock size={16} style={styles.inputIcon} />
                        <input
                          type="date"
                          name="end_date"
                          value={saleForm.end_date}
                          onChange={handleSaleChange}
                          required
                          style={styles.input}
                        />
                      </div>
                    </div>
                  </div>

                  <p style={styles.autoStopNote}>
                    <CheckCircle2 size={14} color="#059669" />
                    <span>
                      <strong>Automatic Life-cycle:</strong> The popup will automatically activate on From Date and <strong>automatically stop showing after End Date</strong>. You also have full privilege to stop it at any time with the switch or button.
                    </span>
                  </p>
                </div>
              </div>

              {/* Row 7: Live Switch & Form Actions */}
              <div style={{ ...styles.fieldGroup, ...styles.fullWidthField }}>
                <div style={styles.submitRow}>
                  <label style={styles.switchContainer}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={saleForm.is_active}
                      onChange={handleSaleChange}
                      style={styles.checkboxInput}
                    />
                    <span
                      style={{
                        ...styles.switchPill,
                        backgroundColor: saleForm.is_active ? "#10b981" : "#cbd5e1",
                      }}
                    >
                      <span
                        style={{
                          ...styles.switchKnob,
                          transform: saleForm.is_active ? "translateX(20px)" : "translateX(2px)",
                        }}
                      />
                    </span>
                    <span style={styles.switchLabel}>
                      <strong>{saleForm.is_active ? "Live / Active" : "Disabled / Stopped"}</strong>
                      <small style={{ display: "block", color: "#64748b", fontSize: "12px", marginTop: "2px" }}>
                        {saleForm.is_active
                          ? "This sale is active and will pop up for visitors during its validity dates."
                          : "This popup is turned off and will not show on the website."}
                      </small>
                    </span>
                  </label>

                  <div style={styles.actionBtnGroup}>
                    <button
                      type="button"
                      onClick={() => handleOpenPreview(saleForm)}
                      style={styles.previewBtn}
                    >
                      <Eye size={16} />
                      <span>Live Preview</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setSaleForm(initialSaleForm);
                        setEditingSaleId(null);
                      }}
                      style={styles.resetBtn}
                    >
                      Reset
                    </button>

                    <button
                      type="submit"
                      disabled={saleSubmitting}
                      style={{
                        ...styles.submitBtn,
                        opacity: saleSubmitting ? 0.75 : 1,
                      }}
                    >
                      {saleSubmitting ? (
                        <>
                          <RefreshCw size={17} className="spin-icon" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Check size={18} />
                          <span>{editingSaleId ? "Update Sale Popup" : "Save & Publish Sale"}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* =====================================================
              SALE POPUPS HISTORY & LIST TABLE
          ===================================================== */}
          <div style={styles.tableCard}>
            <div style={styles.tableCardHeader}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <h2 style={styles.tableTitle}>Configured Sale Popups</h2>
                  <span style={styles.countBadge}>
                    {filteredSalePopups.length} total
                  </span>
                </div>
                <p style={styles.tableSubtitle}>
                  View all active, upcoming, stopped, and expired promotional popups. Stop or re-activate anytime with 1 click.
                </p>
              </div>

              <div style={styles.searchWrapper}>
                <Search size={16} style={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search sale popups..."
                  value={saleSearchTerm}
                  onChange={(e) => setSaleSearchTerm(e.target.value)}
                  style={styles.searchInput}
                />
                {saleSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setSaleSearchTerm("")}
                    style={styles.clearSearchBtn}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {saleLoading ? (
              <div style={styles.loadingContainer}>
                <RefreshCw size={28} color="#D4AF37" className="spin-icon" />
                <p style={{ marginTop: "12px", color: "#64748b", fontWeight: "500" }}>
                  Loading sale popups...
                </p>
              </div>
            ) : filteredSalePopups.length === 0 ? (
              <div style={styles.emptyContainer}>
                <div style={{ ...styles.emptyIconBox, backgroundColor: "rgba(88, 43, 107, 0.1)", borderColor: "#582b6b" }}>
                  <Flame size={34} color="#582b6b" />
                </div>
                <h3 style={styles.emptyTitle}>No Sale Popups Configured Yet</h3>
                <p style={styles.emptySubtitle}>
                  Create your first promotional popup using the form above to welcome shoppers with an exciting offer!
                </p>
              </div>
            ) : (
              <div style={styles.tableResponsive}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>ID</th>
                      <th style={styles.th}>Sale Headline & Offer</th>
                      <th style={styles.th}>Matter / Description</th>
                      <th style={styles.th}>Scope & Target</th>
                      <th style={styles.th}>Validity Period</th>
                      <th style={styles.th}>Status</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSalePopups.map((p, index) => {
                      const statusInfo = getSaleStatusInfo(p);

                      return (
                        <tr
                          key={p.id || index}
                          style={{
                            ...styles.tr,
                            backgroundColor: statusInfo.isLive
                              ? "rgba(16, 185, 129, 0.04)"
                              : index % 2 === 0
                              ? "#ffffff"
                              : "#fafbfb",
                          }}
                        >
                          {/* ID */}
                          <td style={styles.td}>
                            <span style={styles.idBadge}>#{p.id}</span>
                          </td>

                          {/* Headline & Offer */}
                          <td style={styles.td}>
                            <div>
                              <div style={{ fontWeight: "700", color: "#14352b", fontSize: "14px" }}>
                                {p.title}
                              </div>
                              {p.tagline && (
                                <div style={{ fontSize: "12px", color: "#64748b" }}>
                                  {p.tagline}
                                </div>
                              )}
                              <div style={{ marginTop: "4px" }}>
                                <span style={styles.saleOfferBadge}>
                                  {p.offer_badge || `${p.discount_value}${p.discount_type === "percentage" ? "%" : "₹"} OFF`}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Matter */}
                          <td style={styles.td}>
                            <div style={{ maxWidth: "260px", fontSize: "12.5px", color: "#334155", lineHeight: "1.4" }}>
                              {p.description || "-"}
                            </div>
                            {p.coupon_code && (
                              <div style={{ marginTop: "4px" }}>
                                <span style={styles.smallCouponChip}>
                                  <Tag size={11} />
                                  {p.coupon_code}
                                </span>
                              </div>
                            )}
                          </td>

                          {/* Scope */}
                          <td style={styles.td}>
                            {p.apply_type === "category" ? (
                              <span style={styles.scopeCategoryPill}>
                                <Layers size={12} />
                                {p.category_name || "Specific Category"}
                              </span>
                            ) : p.apply_type === "amount" ? (
                              <span style={styles.scopeAmountPill}>
                                <IndianRupee size={12} />
                                Min ₹{Number(p.min_amount).toLocaleString("en-IN")}
                              </span>
                            ) : (
                              <span style={styles.scopeAllPill}>
                                <Sparkles size={12} />
                                All Categories
                              </span>
                            )}
                          </td>

                          {/* Period */}
                          <td style={styles.td}>
                            <div style={{ fontSize: "12.5px", whiteSpace: "nowrap" }}>
                              <div><strong>From:</strong> {formatDate(p.start_date)}</div>
                              <div style={{ color: "#64748b", marginTop: "2px" }}>
                                <strong>To:</strong> {formatDate(p.end_date)}
                              </div>
                            </div>
                          </td>

                          {/* Status */}
                          <td style={styles.td}>
                            <span
                              style={{
                                ...styles.saleStatusChip,
                                backgroundColor: statusInfo.bg,
                                color: statusInfo.color,
                                border: `1px solid ${statusInfo.border}`,
                              }}
                            >
                              {statusInfo.isLive && <span style={styles.pulseDot} />}
                              {statusInfo.label}
                            </span>
                          </td>

                          {/* Actions */}
                          <td style={{ ...styles.td, textAlign: "right" }}>
                            <div style={styles.actionRow}>
                              {/* Live / Stop Toggle Button */}
                              <button
                                type="button"
                                onClick={() => handleToggleSalePopup(p)}
                                disabled={actionLoadingId === p.id}
                                title={p.is_active ? "Stop this sale popup now" : "Activate this sale popup"}
                                style={{
                                  ...styles.tableActionBtn,
                                  backgroundColor: p.is_active ? "#fef2f2" : "#ecfdf5",
                                  borderColor: p.is_active ? "#fecaca" : "#a7f3d0",
                                  color: p.is_active ? "#dc2626" : "#065f46",
                                }}
                              >
                                <Power size={13} />
                                <span>{p.is_active ? "Stop" : "Make Live"}</span>
                              </button>

                              {/* Preview Button */}
                              <button
                                type="button"
                                onClick={() => handleOpenPreview(p)}
                                title="Preview how this appears to website visitors"
                                style={{
                                  ...styles.tableActionBtn,
                                  backgroundColor: "#fdf8ea",
                                  borderColor: "#fde047",
                                  color: "#854d0e",
                                }}
                              >
                                <Eye size={13} />
                                <span>Preview</span>
                              </button>

                              {/* Edit Button */}
                              <button
                                type="button"
                                onClick={() => handleStartEditSale(p)}
                                title="Edit sale popup rules and text"
                                style={{
                                  ...styles.tableActionBtn,
                                  backgroundColor: "#f1f5f9",
                                  borderColor: "#cbd5e1",
                                  color: "#334155",
                                }}
                              >
                                <Edit2 size={13} />
                                <span>Edit</span>
                              </button>

                              {/* Delete Button */}
                              <button
                                type="button"
                                onClick={() => handleDeleteSalePopup(p)}
                                disabled={actionLoadingId === p.id}
                                title="Delete popup"
                                style={{
                                  ...styles.tableActionBtn,
                                  backgroundColor: "#fff1f2",
                                  borderColor: "#fecdd3",
                                  color: "#e11d48",
                                }}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =========================================================================
          TAB 2: STANDARD COUPONS & VOUCHERS
      ========================================================================= */}
      {activeTab === "coupons" && (
        <div>
          {/* STATS SUMMARY ROW */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIconWrapper, background: "rgba(20, 53, 43, 0.08)", color: "#14352b" }}>
                <TicketPercent size={22} />
              </div>
              <div>
                <div style={styles.statLabel}>Total Coupons</div>
                <div style={styles.statValue}>{metrics.total}</div>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={{ ...styles.statIconWrapper, background: "rgba(16, 185, 129, 0.12)", color: "#10b981" }}>
                <CheckCircle2 size={22} />
              </div>
              <div>
                <div style={styles.statLabel}>Active & Redeemable</div>
                <div style={{ ...styles.statValue, color: "#065f46" }}>{metrics.active}</div>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={{ ...styles.statIconWrapper, background: "rgba(220, 38, 38, 0.1)", color: "#dc2626" }}>
                <Power size={22} />
              </div>
              <div>
                <div style={styles.statLabel}>Disabled by Admin</div>
                <div style={{ ...styles.statValue, color: metrics.disabled > 0 ? "#dc2626" : "#64748b" }}>
                  {metrics.disabled}
                </div>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={{ ...styles.statIconWrapper, background: "rgba(239, 68, 68, 0.1)", color: "#ef4444" }}>
                <Clock size={22} />
              </div>
              <div>
                <div style={styles.statLabel}>Expired / Past Date</div>
                <div style={{ ...styles.statValue, color: metrics.expired > 0 ? "#dc2626" : "#64748b" }}>
                  {metrics.expired}
                </div>
              </div>
            </div>
          </div>

          {/* ADD COUPON FORM CARD */}
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <div style={styles.cardHeaderIcon}>
                <Plus size={20} color="#d4af37" />
              </div>
              <div>
                <h2 style={styles.cardTitle}>Create New Discount Coupon</h2>
                <p style={styles.cardSubtitle}>
                  Configure promo codes for shopping cart, saree categories, or individual products.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} style={styles.formGrid}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Coupon Code</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  <Tag size={17} style={styles.inputIcon} />
                  <input
                    type="text"
                    name="code"
                    value={form.code}
                    onChange={handleChange}
                    placeholder="e.g. FESTIVE20, VANYA500"
                    required
                    style={{ ...styles.input, textTransform: "uppercase", fontWeight: "700", letterSpacing: "1px" }}
                  />
                </div>
                <span style={styles.helperText}>Auto-converted to uppercase</span>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Discount Type</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.typeSelectorGroup}>
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, discount_type: "percentage" }))}
                    style={{
                      ...styles.typeSelectorBtn,
                      ...(form.discount_type === "percentage" ? styles.typeSelectorBtnActive : {}),
                    }}
                  >
                    <Percent size={16} />
                    <span>Percentage (%)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, discount_type: "fixed" }))}
                    style={{
                      ...styles.typeSelectorBtn,
                      ...(form.discount_type === "fixed" ? styles.typeSelectorBtnActive : {}),
                    }}
                  >
                    <IndianRupee size={16} />
                    <span>Fixed Amount (₹)</span>
                  </button>
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Discount Value</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  {form.discount_type === "percentage" ? (
                    <Percent size={17} style={styles.inputIcon} />
                  ) : (
                    <IndianRupee size={17} style={styles.inputIcon} />
                  )}
                  <input
                    type="number"
                    step="any"
                    min="0"
                    max={form.discount_type === "percentage" ? "100" : undefined}
                    name="discount_value"
                    value={form.discount_value}
                    onChange={handleChange}
                    placeholder={form.discount_type === "percentage" ? "e.g. 15 (for 15%)" : "e.g. 500 (for ₹500)"}
                    required
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Apply Scope</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  <ShoppingBag size={17} style={styles.inputIcon} />
                  <select
                    name="apply_type"
                    value={form.apply_type}
                    onChange={handleChange}
                    style={styles.select}
                  >
                    <option value="cart">Entire Cart (Global)</option>
                    <option value="category">Specific Saree Category</option>
                    <option value="product">Specific Product ID</option>
                  </select>
                </div>
              </div>

              {form.apply_type === "category" && (
                <div style={{ ...styles.fieldGroup, ...styles.conditionalField }}>
                  <label style={styles.label}>
                    <span>Category Name</span>
                    <span style={styles.requiredStar}>*</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <Layers size={17} style={styles.inputIcon} />
                    <input
                      type="text"
                      name="category_name"
                      value={form.category_name}
                      onChange={handleChange}
                      list="category-suggestions"
                      placeholder="e.g. Silk Sarees, Pattu Sarees"
                      required
                      style={styles.input}
                    />
                    <datalist id="category-suggestions">
                      {categories.map((c, i) => (
                        <option key={c.id || i} value={c.category} />
                      ))}
                    </datalist>
                  </div>
                </div>
              )}

              {form.apply_type === "product" && (
                <div style={{ ...styles.fieldGroup, ...styles.conditionalField }}>
                  <label style={styles.label}>
                    <span>Product ID</span>
                    <span style={styles.requiredStar}>*</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <Tag size={17} style={styles.inputIcon} />
                    <input
                      type="number"
                      name="product_id"
                      value={form.product_id}
                      onChange={handleChange}
                      placeholder="e.g. 104"
                      required
                      style={styles.input}
                    />
                  </div>
                </div>
              )}

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Minimum Cart Amount (₹)</span>
                </label>
                <div style={styles.inputWrapper}>
                  <IndianRupee size={17} style={styles.inputIcon} />
                  <input
                    type="number"
                    step="any"
                    min="0"
                    name="min_amount"
                    value={form.min_amount}
                    onChange={handleChange}
                    placeholder="e.g. 1000 (Optional)"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={styles.label}>
                    <span>Expiry Date</span>
                  </label>
                  <div style={styles.presetDateGroup}>
                    <button type="button" onClick={() => setExpiryDays(30)} style={styles.presetDateBtn}>
                      +30d
                    </button>
                    <button type="button" onClick={() => setExpiryDays(60)} style={styles.presetDateBtn}>
                      +60d
                    </button>
                    <button type="button" onClick={() => setExpiryDays(90)} style={styles.presetDateBtn}>
                      +90d
                    </button>
                  </div>
                </div>
                <div style={styles.inputWrapper}>
                  <Calendar size={17} style={styles.inputIcon} />
                  <input
                    type="date"
                    name="expiry_date"
                    value={form.expiry_date}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ ...styles.fieldGroup, ...styles.fullWidthField }}>
                <div style={styles.submitRow}>
                  <label style={styles.switchContainer}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={form.is_active}
                      onChange={handleChange}
                      style={styles.checkboxInput}
                    />
                    <span
                      style={{
                        ...styles.switchPill,
                        backgroundColor: form.is_active ? "#10b981" : "#cbd5e1",
                      }}
                    >
                      <span
                        style={{
                          ...styles.switchKnob,
                          transform: form.is_active ? "translateX(20px)" : "translateX(2px)",
                        }}
                      />
                    </span>
                    <span style={styles.switchLabel}>
                      <strong>Coupon is Active</strong>
                    </span>
                  </label>

                  <div style={styles.actionBtnGroup}>
                    <button
                      type="button"
                      onClick={() =>
                        setForm({
                          code: "",
                          discount_type: "percentage",
                          discount_value: "",
                          apply_type: "cart",
                          category_name: "",
                          product_id: "",
                          min_amount: "",
                          expiry_date: "",
                          is_active: true,
                        })
                      }
                      style={styles.resetBtn}
                    >
                      Reset
                    </button>

                    <button
                      type="submit"
                      disabled={submitting}
                      style={{
                        ...styles.submitBtn,
                        opacity: submitting ? 0.75 : 1,
                      }}
                    >
                      {submitting ? (
                        <>
                          <RefreshCw size={17} className="spin-icon" />
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <Plus size={18} />
                          <span>Add Coupon</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* EXISTING COUPONS TABLE */}
          <div style={styles.tableCard}>
            <div style={styles.tableCardHeader}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <h2 style={styles.tableTitle}>Existing Coupons</h2>
                  <span style={styles.countBadge}>
                    {filteredCoupons.length} of {coupons.length}
                  </span>
                </div>
              </div>

              <div style={styles.toolbar}>
                <div style={styles.filterTabs}>
                  <button
                    type="button"
                    onClick={() => setStatusFilter("all")}
                    style={{
                      ...styles.filterTab,
                      ...(statusFilter === "all" ? styles.filterTabActive : {}),
                    }}
                  >
                    All ({coupons.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter("active")}
                    style={{
                      ...styles.filterTab,
                      ...(statusFilter === "active" ? styles.filterTabActive : {}),
                    }}
                  >
                    Active ({metrics.active})
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter("disabled")}
                    style={{
                      ...styles.filterTab,
                      ...(statusFilter === "disabled" ? styles.filterTabActive : {}),
                    }}
                  >
                    Disabled ({metrics.disabled})
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter("expired")}
                    style={{
                      ...styles.filterTab,
                      ...(statusFilter === "expired" ? styles.filterTabActive : {}),
                    }}
                  >
                    Expired ({metrics.expired})
                  </button>
                </div>

                <div style={styles.searchWrapper}>
                  <Search size={16} style={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search coupons..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      style={styles.clearSearchBtn}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {loading ? (
              <div style={styles.loadingContainer}>
                <RefreshCw size={28} color="#D4AF37" className="spin-icon" />
                <p style={{ marginTop: "12px", color: "#64748b", fontWeight: "500" }}>
                  Loading coupons catalog...
                </p>
              </div>
            ) : filteredCoupons.length === 0 ? (
              <div style={styles.emptyContainer}>
                <div style={styles.emptyIconBox}>
                  <TicketPercent size={36} color="#d4af37" />
                </div>
                <h3 style={styles.emptyTitle}>No coupons found</h3>
              </div>
            ) : (
              <div style={styles.tableResponsive}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>ID</th>
                      <th style={styles.th}>Coupon Code</th>
                      <th style={styles.th}>Discount</th>
                      <th style={styles.th}>Apply Scope</th>
                      <th style={styles.th}>Target Details</th>
                      <th style={styles.th}>Min Purchase</th>
                      <th style={styles.th}>Expiry Date</th>
                      <th style={styles.th}>Status</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCoupons.map((c, index) => {
                      const expired = isExpired(c.expiry_date);
                      const isCurrentActive = c.is_active && !expired;

                      return (
                        <tr
                          key={c.id || index}
                          style={{
                            ...styles.tr,
                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafbfb",
                          }}
                        >
                          <td style={styles.td}>
                            <span style={styles.idBadge}>#{c.id}</span>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.codeCell}>
                              <span style={styles.codeBadge}>
                                <Tag size={13} style={{ marginRight: 5, opacity: 0.8 }} />
                                {c.code}
                              </span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.discountPill}>
                              {c.discount_type === "percentage" ? (
                                <span style={styles.percentageBadge}>
                                  <Percent size={13} style={{ marginRight: 3 }} />
                                  {parseFloat(c.discount_value)}% OFF
                                </span>
                              ) : (
                                <span style={styles.fixedBadge}>
                                  <IndianRupee size={13} style={{ marginRight: 1 }} />
                                  {parseFloat(c.discount_value).toLocaleString("en-IN")} FLAT
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                ...styles.scopeBadge,
                                ...(c.apply_type === "cart"
                                  ? styles.scopeCart
                                  : c.apply_type === "category"
                                  ? styles.scopeCategory
                                  : styles.scopeProduct),
                              }}
                            >
                              {c.apply_type === "cart" && "Cart Wide"}
                              {c.apply_type === "category" && "Category"}
                              {c.apply_type === "product" && "Single Product"}
                            </span>
                          </td>
                          <td style={styles.td}>
                            {c.apply_type === "category" && c.category_name ? (
                              <div style={styles.targetCell}>
                                <Layers size={13} color="#8b5cf6" />
                                <span style={{ fontWeight: "600", color: "#1e293b" }}>
                                  {c.category_name}
                                </span>
                              </div>
                            ) : c.apply_type === "product" && c.product_id ? (
                              <div style={styles.targetCell}>
                                <ShoppingBag size={13} color="#2563eb" />
                                <span>Product #{c.product_id}</span>
                              </div>
                            ) : (
                              <span style={{ color: "#94a3b8", fontSize: "13px" }}>All Items</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {c.min_amount && Number(c.min_amount) > 0 ? (
                              <span style={styles.minAmountText}>
                                ₹{parseFloat(c.min_amount).toLocaleString("en-IN")}
                              </span>
                            ) : (
                              <span style={{ color: "#94a3b8", fontSize: "13px" }}>No Minimum</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.expiryCell}>
                              <span
                                style={{
                                  ...styles.expiryDate,
                                  color: expired ? "#dc2626" : "#334155",
                                  fontWeight: expired ? "600" : "500",
                                }}
                              >
                                {formatDate(c.expiry_date)}
                              </span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.statusCell}>
                              <span
                                style={{
                                  ...styles.statusDot,
                                  backgroundColor: isCurrentActive ? "#10b981" : "#94a3b8",
                                }}
                              />
                              <span
                                style={{
                                  ...styles.statusText,
                                  color: isCurrentActive ? "#065f46" : "#64748b",
                                  fontWeight: "600",
                                }}
                              >
                                {isCurrentActive ? "Active" : c.is_active && expired ? "Expired" : "Disabled"}
                              </span>
                            </div>
                          </td>
                          <td style={{ ...styles.td, textAlign: "right" }}>
                            <div style={styles.actionRow}>
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(c)}
                                disabled={actionLoadingId === c.id}
                                title={c.is_active ? "Disable" : "Enable"}
                                style={{
                                  ...styles.tableActionBtn,
                                  backgroundColor: c.is_active ? "#ecfdf5" : "#f1f5f9",
                                  borderColor: c.is_active ? "#a7f3d0" : "#cbd5e1",
                                  color: c.is_active ? "#065f46" : "#64748b",
                                }}
                              >
                                <Power size={13} color={c.is_active ? "#10b981" : "#94a3b8"} />
                                <span>{c.is_active ? "Active" : "Disabled"}</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleStartEdit(c)}
                                title="Edit"
                                style={{
                                  ...styles.tableActionBtn,
                                  backgroundColor: "#fef9c3",
                                  borderColor: "#fde047",
                                  color: "#854d0e",
                                }}
                              >
                                <Edit2 size={13} />
                                <span>Edit</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDeleteCoupon(c)}
                                disabled={actionLoadingId === c.id}
                                title="Delete"
                                style={{
                                  ...styles.tableActionBtn,
                                  backgroundColor: "#fef2f2",
                                  borderColor: "#fecaca",
                                  color: "#dc2626",
                                }}
                              >
                                <Trash2 size={13} />
                                <span>Delete</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleCopyCode(c.code)}
                                title="Copy"
                                style={{
                                  ...styles.copyBtn,
                                  backgroundColor: copiedCode === c.code ? "#ecfdf5" : "#f1f5f9",
                                  color: copiedCode === c.code ? "#059669" : "#334155",
                                }}
                              >
                                {copiedCode === c.code ? <Check size={13} /> : <Copy size={13} />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =====================================================
          EDIT COUPON MODAL
      ===================================================== */}
      {editingCoupon && editForm && (
        <div
          style={styles.modalOverlay}
          onClick={() => !editSubmitting && setEditingCoupon(null)}
        >
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={styles.modalHeaderIcon}>
                  <Edit2 size={18} color="#d4af37" />
                </div>
                <div>
                  <h3 style={styles.modalTitle}>Edit Coupon: {editingCoupon.code}</h3>
                  <p style={styles.modalSubtitle}>Modify discount rules and validity.</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setEditingCoupon(null)}
                style={styles.modalCloseBtn}
                disabled={editSubmitting}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} style={styles.formGrid}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Coupon Code</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  <Tag size={17} style={styles.inputIcon} />
                  <input
                    type="text"
                    name="code"
                    value={editForm.code}
                    onChange={handleEditChange}
                    required
                    style={{ ...styles.input, textTransform: "uppercase", fontWeight: "700" }}
                  />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Discount Type</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.typeSelectorGroup}>
                  <button
                    type="button"
                    onClick={() => setEditForm((p) => ({ ...p, discount_type: "percentage" }))}
                    style={{
                      ...styles.typeSelectorBtn,
                      ...(editForm.discount_type === "percentage" ? styles.typeSelectorBtnActive : {}),
                    }}
                  >
                    <Percent size={15} />
                    <span>Percentage (%)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm((p) => ({ ...p, discount_type: "fixed" }))}
                    style={{
                      ...styles.typeSelectorBtn,
                      ...(editForm.discount_type === "fixed" ? styles.typeSelectorBtnActive : {}),
                    }}
                  >
                    <IndianRupee size={15} />
                    <span>Fixed (₹)</span>
                  </button>
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Discount Value</span>
                  <span style={styles.requiredStar}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  {editForm.discount_type === "percentage" ? (
                    <Percent size={17} style={styles.inputIcon} />
                  ) : (
                    <IndianRupee size={17} style={styles.inputIcon} />
                  )}
                  <input
                    type="number"
                    step="any"
                    min="0"
                    max={editForm.discount_type === "percentage" ? "100" : undefined}
                    name="discount_value"
                    value={editForm.discount_value}
                    onChange={handleEditChange}
                    required
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Apply Scope</span>
                </label>
                <div style={styles.inputWrapper}>
                  <ShoppingBag size={17} style={styles.inputIcon} />
                  <select
                    name="apply_type"
                    value={editForm.apply_type}
                    onChange={handleEditChange}
                    style={styles.select}
                  >
                    <option value="cart">Entire Cart (Global)</option>
                    <option value="category">Specific Saree Category</option>
                    <option value="product">Specific Product ID</option>
                  </select>
                </div>
              </div>

              {editForm.apply_type === "category" && (
                <div style={{ ...styles.fieldGroup, ...styles.conditionalField }}>
                  <label style={styles.label}>
                    <span>Category Name</span>
                    <span style={styles.requiredStar}>*</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <Layers size={17} style={styles.inputIcon} />
                    <input
                      type="text"
                      name="category_name"
                      value={editForm.category_name}
                      onChange={handleEditChange}
                      list="edit-category-suggestions"
                      placeholder="e.g. Silk Sarees"
                      required
                      style={styles.input}
                    />
                    <datalist id="edit-category-suggestions">
                      {categories.map((c, i) => (
                        <option key={c.id || i} value={c.category} />
                      ))}
                    </datalist>
                  </div>
                </div>
              )}

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Minimum Cart Amount (₹)</span>
                </label>
                <div style={styles.inputWrapper}>
                  <IndianRupee size={17} style={styles.inputIcon} />
                  <input
                    type="number"
                    step="any"
                    min="0"
                    name="min_amount"
                    value={editForm.min_amount}
                    onChange={handleEditChange}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <span>Expiry Date</span>
                </label>
                <div style={styles.inputWrapper}>
                  <Calendar size={17} style={styles.inputIcon} />
                  <input
                    type="date"
                    name="expiry_date"
                    value={editForm.expiry_date}
                    onChange={handleEditChange}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ ...styles.fieldGroup, ...styles.fullWidthField }}>
                <label style={styles.switchContainer}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={editForm.is_active}
                    onChange={handleEditChange}
                    style={styles.checkboxInput}
                  />
                  <span
                    style={{
                      ...styles.switchPill,
                      backgroundColor: editForm.is_active ? "#10b981" : "#cbd5e1",
                    }}
                  >
                    <span
                      style={{
                        ...styles.switchKnob,
                        transform: editForm.is_active ? "translateX(20px)" : "translateX(2px)",
                      }}
                    />
                  </span>
                  <span style={styles.switchLabel}>
                    <strong>Coupon is {editForm.is_active ? "Active" : "Disabled"}</strong>
                  </span>
                </label>
              </div>

              <div
                style={{
                  ...styles.fullWidthField,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <button
                  type="button"
                  disabled={editSubmitting}
                  onClick={() => setEditingCoupon(null)}
                  style={styles.resetBtn}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={editSubmitting}
                  style={{
                    ...styles.submitBtn,
                    opacity: editSubmitting ? 0.75 : 1,
                  }}
                >
                  {editSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          INTERACTIVE LIVE PREVIEW MODAL (EXACT WEBSITE POPUP EXPERIENCE)
      ========================================================================= */}
      {previewModalOpen && previewData && (
        <div style={styles.previewOverlay} onClick={() => setPreviewModalOpen(false)}>
          <div style={styles.previewContainer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.previewDeviceHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ef4444", display: "inline-block" }} />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#f59e0b", display: "inline-block" }} />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#10b981", display: "inline-block" }} />
                <span style={{ fontSize: "12px", color: "#64748b", marginLeft: "10px", fontWeight: "600" }}>
                  Website Visitor View — Vanya Collections
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewModalOpen(false)}
                style={styles.previewCloseBtn}
              >
                <X size={16} />
              </button>
            </div>

            {/* The Royal Theme Popup Container */}
            <div style={styles.royalPreviewModal}>
              <div style={styles.royalGlowTop} />

              <div style={styles.royalTagPill}>
                <Sparkles size={14} color="#fbd349" />
                <span>SALE FOR THE DAY</span>
              </div>

              <h2 style={styles.royalTitle}>{previewData.title || "Sale of the Day!"}</h2>

              {previewData.tagline && (
                <p style={styles.royalTagline}>{previewData.tagline}</p>
              )}

              {previewData.offer_badge && (
                <div>
                  <div style={styles.royalDiscountBadge}>
                    {previewData.offer_badge}
                  </div>
                </div>
              )}

              {/* Scope Chip */}
              <div>
                <span style={styles.royalScopeChip}>
                  {previewData.apply_type === "category" ? (
                    <>
                      <Layers size={13} color="#fbd349" />
                      Special Offer on {previewData.category_name || "Selected"} Sarees
                    </>
                  ) : previewData.apply_type === "amount" ? (
                    <>
                      <ShoppingBag size={13} color="#fbd349" />
                      On orders above ₹{Number(previewData.min_amount || 0).toLocaleString("en-IN")}
                    </>
                  ) : (
                    <>
                      <Sparkles size={13} color="#fbd349" />
                      Valid on All Royal Saree Collections
                    </>
                  )}
                </span>
              </div>

              {previewData.description && (
                <p style={styles.royalDesc}>{previewData.description}</p>
              )}

              {previewData.coupon_code && (
                <div style={styles.royalCouponStrip}>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "11px", color: "#f3e5ab", letterSpacing: "1px", textTransform: "uppercase", fontWeight: "700" }}>
                      Promo Code
                    </div>
                    <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#ffffff", letterSpacing: "1.5px", fontFamily: "monospace" }}>
                      {previewData.coupon_code}
                    </div>
                  </div>
                  <div style={styles.royalCopyBtn}>
                    <Copy size={13} />
                    <span>COPY CODE</span>
                  </div>
                </div>
              )}


              <div style={styles.royalCtaBtn}>
                <span>{previewData.cta_text || "Shop Royal Sarees Now"}</span>
                <ArrowRight size={17} />
              </div>

              <div style={{ marginTop: "12px", fontSize: "12px", color: "rgba(255,255,255,0.6)", textDecoration: "underline" }}>
                No thanks, continue browsing
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global Embedded Styles */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================
const styles = {
  pageContainer: {
    maxWidth: "1250px",
    margin: "0 auto",
    fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
    color: "#1e293b",
    paddingBottom: "40px",
  },

  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "16px",
  },

  badgeLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1.2px",
    color: "#D4AF37",
    backgroundColor: "#14352b",
    padding: "4px 10px",
    borderRadius: "20px",
    marginBottom: "8px",
  },

  title: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 6px 0",
    letterSpacing: "-0.5px",
  },

  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
    maxWidth: "700px",
    lineHeight: "1.5",
  },

  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 16px",
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    border: "1.5px solid #e2e8f0",
    color: "#14352b",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    transition: "all 0.2s ease",
  },

  /* TAB NAVIGATION */
  tabNavContainer: {
    display: "flex",
    gap: "10px",
    marginBottom: "24px",
    borderBottom: "2px solid #e2e8f0",
    paddingBottom: "12px",
    flexWrap: "wrap",
  },

  mainTabBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "9px",
    padding: "12px 20px",
    borderRadius: "12px",
    backgroundColor: "#f8fafc",
    border: "1.5px solid #e2e8f0",
    color: "#475569",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  mainTabBtnActive: {
    backgroundColor: "#14352b",
    color: "#ffffff",
    borderColor: "#14352b",
    boxShadow: "0 4px 14px rgba(20, 53, 43, 0.25)",
  },

  tabCountBadge: {
    fontSize: "11px",
    fontWeight: "700",
    padding: "2px 7px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "inherit",
    border: "1px solid rgba(255, 255, 255, 0.3)",
  },

  livePulsePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "11px",
    fontWeight: "800",
    padding: "2px 8px",
    borderRadius: "12px",
    backgroundColor: "#10b981",
    color: "#ffffff",
    letterSpacing: "0.5px",
  },

  pulseDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: "#10b981",
    display: "inline-block",
    animation: "pulseGlow 1.5s infinite",
  },

  pulseDotWhite: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    display: "inline-block",
    animation: "pulseGlow 1.5s infinite",
  },

  feedbackAlert: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 18px",
    borderRadius: "10px",
    border: "1px solid",
    marginBottom: "22px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
  },

  /* LIVE STATUS HERO BANNER */
  liveStatusBanner: {
    borderRadius: "18px",
    padding: "24px 26px",
    border: "2px solid",
    marginBottom: "28px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "20px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.06)",
  },

  liveBannerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: "18px",
    flex: 1,
    minWidth: "300px",
  },

  liveBannerIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  liveStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "800",
    letterSpacing: "0.8px",
  },

  liveBannerTitle: {
    fontSize: "20px",
    fontWeight: "800",
    margin: "8px 0 4px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },

  liveBannerOfferBadge: {
    fontSize: "13px",
    fontWeight: "800",
    backgroundColor: "#D4AF37",
    color: "#2e0f3c",
    padding: "3px 10px",
    borderRadius: "20px",
    letterSpacing: "0.5px",
  },

  liveBannerDesc: {
    fontSize: "13.5px",
    margin: 0,
    lineHeight: "1.5",
    maxWidth: "600px",
  },

  liveBannerActions: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    alignItems: "flex-end",
  },

  stopSaleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(220, 38, 38, 0.35)",
    transition: "all 0.2s ease",
  },

  previewLiveBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    color: "#ffffff",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    padding: "8px 14px",
    borderRadius: "9px",
    fontSize: "12.5px",
    fontWeight: "600",
    cursor: "pointer",
  },

  /* FORM CARD */
  formCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "28px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
    marginBottom: "32px",
    position: "relative",
  },

  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    paddingBottom: "20px",
    marginBottom: "24px",
    borderBottom: "1px solid #f1f5f9",
  },

  cardHeaderIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    backgroundColor: "#14352b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 3px 8px rgba(20, 53, 43, 0.2)",
  },

  cardTitle: {
    fontSize: "19px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 4px 0",
  },

  cardSubtitle: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
    lineHeight: "1.4",
  },

  cancelEditBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "5px 10px",
    borderRadius: "6px",
    backgroundColor: "#f1f5f9",
    border: "1px solid #cbd5e1",
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569",
    cursor: "pointer",
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
  },

  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  conditionalField: {
    backgroundColor: "#f8fafc",
    padding: "12px",
    borderRadius: "10px",
    border: "1px dashed #cbd5e1",
  },

  fullWidthField: {
    gridColumn: "1 / -1",
    marginTop: "6px",
  },

  label: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#334155",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  subLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#475569",
    marginBottom: "4px",
    display: "block",
  },

  requiredStar: {
    color: "#ef4444",
    fontSize: "14px",
  },

  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },

  inputIcon: {
    position: "absolute",
    left: "14px",
    color: "#94a3b8",
    pointerEvents: "none",
  },

  input: {
    width: "100%",
    padding: "12px 14px 12px 42px",
    borderRadius: "10px",
    border: "1.5px solid #cbd5e1",
    fontSize: "14px",
    color: "#0f172a",
    backgroundColor: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
  },

  inputNoIcon: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1.5px solid #cbd5e1",
    fontSize: "14px",
    color: "#0f172a",
    backgroundColor: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
  },

  select: {
    width: "100%",
    padding: "12px 14px 12px 42px",
    borderRadius: "10px",
    border: "1.5px solid #cbd5e1",
    fontSize: "14px",
    color: "#0f172a",
    backgroundColor: "#ffffff",
    outline: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1.5px solid #cbd5e1",
    fontSize: "14px",
    color: "#0f172a",
    backgroundColor: "#ffffff",
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
    fontFamily: "inherit",
    lineHeight: "1.5",
  },

  helperText: {
    fontSize: "11px",
    color: "#94a3b8",
  },

  typeSelectorGroup: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },

  typeSelectorBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
    padding: "11px 12px",
    borderRadius: "10px",
    border: "1.5px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },

  typeSelectorBtnActive: {
    backgroundColor: "#14352b",
    color: "#D4AF37",
    borderColor: "#14352b",
    boxShadow: "0 3px 10px rgba(20, 53, 43, 0.2)",
  },

  /* PERIOD ADJUSTMENT CARD */
  periodAdjustmentCard: {
    backgroundColor: "#fdf8ea",
    border: "1.5px solid #fde047",
    borderRadius: "14px",
    padding: "18px 20px",
  },

  periodHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "14px",
  },

  presetButtonsRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  },

  presetChip: {
    backgroundColor: "#ffffff",
    border: "1px solid #d4af37",
    borderRadius: "8px",
    padding: "4px 10px",
    fontSize: "11.5px",
    fontWeight: "700",
    color: "#854d0e",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },

  periodInputsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
  },

  dateField: {
    display: "flex",
    flexDirection: "column",
  },

  autoStopNote: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: "#065f46",
    marginTop: "12px",
    marginBottom: 0,
    backgroundColor: "#ecfdf5",
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #a7f3d0",
  },

  submitRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
    paddingTop: "14px",
    borderTop: "1px solid #f1f5f9",
  },

  switchContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    userSelect: "none",
  },

  checkboxInput: {
    display: "none",
  },

  switchPill: {
    width: "44px",
    height: "24px",
    borderRadius: "14px",
    position: "relative",
    display: "inline-block",
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.15)",
  },

  switchKnob: {
    position: "absolute",
    top: "3px",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
    transition: "transform 0.25s ease",
  },

  switchLabel: {
    fontSize: "14px",
    color: "#1e293b",
  },

  actionBtnGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },

  previewBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    padding: "12px 18px",
    borderRadius: "10px",
    backgroundColor: "#fdf8ea",
    border: "1.5px solid #d4af37",
    color: "#854d0e",
    fontSize: "13.5px",
    fontWeight: "700",
    cursor: "pointer",
  },

  resetBtn: {
    padding: "12px 18px",
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    border: "1.5px solid #cbd5e1",
    color: "#475569",
    fontSize: "13.5px",
    fontWeight: "700",
    cursor: "pointer",
  },

  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 24px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #14352b 0%, #1f4a3c 100%)",
    color: "#D4AF37",
    border: "1px solid #14352b",
    fontSize: "14px",
    fontWeight: "800",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(20, 53, 43, 0.25)",
  },

  /* TABLE CARD */
  tableCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
  },

  tableCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "16px",
  },

  tableTitle: {
    fontSize: "19px",
    fontWeight: "800",
    color: "#14352b",
    margin: 0,
  },

  countBadge: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#065f46",
    backgroundColor: "#ecfdf5",
    padding: "2px 8px",
    borderRadius: "20px",
    border: "1px solid #a7f3d0",
  },

  tableSubtitle: {
    fontSize: "13px",
    color: "#64748b",
    margin: "4px 0 0 0",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },

  filterTabs: {
    display: "flex",
    backgroundColor: "#f1f5f9",
    padding: "3px",
    borderRadius: "9px",
    gap: "3px",
  },

  filterTab: {
    background: "transparent",
    border: "none",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    borderRadius: "7px",
    cursor: "pointer",
  },

  filterTabActive: {
    backgroundColor: "#ffffff",
    color: "#14352b",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },

  searchWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    minWidth: "240px",
  },

  searchIcon: {
    position: "absolute",
    left: "11px",
    color: "#94a3b8",
  },

  searchInput: {
    width: "100%",
    padding: "8px 28px 8px 34px",
    borderRadius: "9px",
    border: "1.5px solid #cbd5e1",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  },

  clearSearchBtn: {
    position: "absolute",
    right: "8px",
    background: "transparent",
    border: "none",
    fontSize: "16px",
    color: "#94a3b8",
    cursor: "pointer",
  },

  tableResponsive: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    borderRadius: "12px",
    border: "1px solid #edf2f7",
    width: "100%",
  },

  table: {
    width: "100%",
    minWidth: "850px",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "13.5px",
  },

  th: {
    padding: "14px 16px",
    backgroundColor: "#14352b",
    color: "#f5d470",
    fontWeight: "700",
    fontSize: "12px",
    textAlign: "left",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },

  tr: {
    transition: "background-color 0.15s ease",
  },

  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #edf2f7",
    verticalAlign: "middle",
    color: "#334155",
  },

  idBadge: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#94a3b8",
    fontFamily: "monospace",
  },

  codeCell: {
    display: "flex",
    alignItems: "center",
  },

  codeBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "6px",
    backgroundColor: "#fdf8ea",
    border: "1.5px dashed #d4af37",
    color: "#8a6d1c",
    fontWeight: "800",
    fontSize: "13px",
    fontFamily: "monospace",
  },

  saleOfferBadge: {
    display: "inline-block",
    padding: "3px 9px",
    borderRadius: "14px",
    backgroundColor: "#fdf8ea",
    border: "1px solid #fde047",
    color: "#854d0e",
    fontWeight: "800",
    fontSize: "11.5px",
  },

  smallCouponChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    backgroundColor: "#f1f5f9",
    padding: "2px 7px",
    borderRadius: "5px",
    border: "1px solid #e2e8f0",
  },

  scopeAllPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 8px",
    borderRadius: "6px",
    fontSize: "11.5px",
    fontWeight: "700",
    backgroundColor: "#ecfdf5",
    color: "#065f46",
    border: "1px solid #a7f3d0",
  },

  scopeCategoryPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 8px",
    borderRadius: "6px",
    fontSize: "11.5px",
    fontWeight: "700",
    backgroundColor: "#faf5ff",
    color: "#7e22ce",
    border: "1px solid #e9d5ff",
  },

  scopeAmountPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 8px",
    borderRadius: "6px",
    fontSize: "11.5px",
    fontWeight: "700",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
  },

  saleStatusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "14px",
    fontSize: "11.5px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  actionRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "nowrap",
  },

  tableActionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "5px 9px",
    borderRadius: "6px",
    border: "1px solid",
    fontSize: "11.5px",
    fontWeight: "700",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  copyBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "5px 9px",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    fontSize: "11.5px",
    cursor: "pointer",
  },

  discountPill: {
    display: "inline-block",
  },

  percentageBadge: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
    padding: "3px 8px",
    borderRadius: "6px",
    fontWeight: "700",
    fontSize: "12px",
  },

  fixedBadge: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#f0fdf4",
    color: "#15803d",
    border: "1px solid #bbf7d0",
    padding: "3px 8px",
    borderRadius: "6px",
    fontWeight: "700",
    fontSize: "12px",
  },

  scopeBadge: {
    display: "inline-block",
    padding: "3px 8px",
    borderRadius: "6px",
    fontSize: "11px",
    fontWeight: "700",
    textTransform: "uppercase",
  },

  scopeCart: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
  },

  scopeCategory: {
    backgroundColor: "#faf5ff",
    color: "#7e22ce",
    border: "1px solid #e9d5ff",
  },

  scopeProduct: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
  },

  targetCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
  },

  minAmountText: {
    fontWeight: "600",
    color: "#0f172a",
  },

  expiryCell: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
  },

  expiryDate: {
    fontSize: "13px",
  },

  statusCell: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
  },

  statusDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
  },

  statusText: {
    fontSize: "12px",
  },

  presetDateGroup: {
    display: "flex",
    gap: "4px",
  },

  presetDateBtn: {
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    padding: "2px 8px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    cursor: "pointer",
  },

  /* STATS ROW */
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },

  statCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "18px 20px",
    border: "1px solid #edf2f7",
    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },

  statIconWrapper: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  statLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    marginBottom: "4px",
    textTransform: "uppercase",
  },

  statValue: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#0f172a",
    lineHeight: "1.1",
  },

  /* MODALS */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.65)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "20px",
  },

  modalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "18px",
    padding: "28px",
    width: "100%",
    maxWidth: "750px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
    border: "1px solid #e2e8f0",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px solid #f1f5f9",
  },

  modalHeaderIcon: {
    width: "38px",
    height: "38px",
    borderRadius: "10px",
    backgroundColor: "#14352b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  modalTitle: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 3px 0",
  },

  modalSubtitle: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
  },

  modalCloseBtn: {
    background: "#f1f5f9",
    border: "none",
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#475569",
  },

  loadingContainer: {
    padding: "60px 20px",
    textAlign: "center",
  },

  emptyContainer: {
    padding: "50px 20px",
    textAlign: "center",
  },

  emptyIconBox: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: "#fdf8ea",
    border: "2px solid #d4af37",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "14px",
  },

  emptyTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0 0 6px 0",
  },

  emptySubtitle: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
  },

  /* INTERACTIVE PREVIEW MODAL STYLES */
  previewOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(10, 5, 20, 0.8)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999999,
    padding: "20px",
  },

  previewContainer: {
    width: "100%",
    maxWidth: "540px",
    borderRadius: "24px",
    overflow: "hidden",
    boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
    backgroundColor: "#1e1e2d",
    border: "1.5px solid rgba(212, 175, 55, 0.4)",
  },

  previewDeviceHeader: {
    backgroundColor: "#161622",
    padding: "12px 18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },

  previewCloseBtn: {
    background: "transparent",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
  },

  royalPreviewModal: {
    background: "radial-gradient(circle at 50% 0%, #582b6b 0%, #361445 100%)",
    padding: "36px 30px 32px",
    textAlign: "center",
    position: "relative",
    color: "#ffffff",
    fontFamily: "'Playfair Display', serif",
  },

  royalGlowTop: {
    position: "absolute",
    top: "-40px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "240px",
    height: "120px",
    background: "radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 70%)",
    pointerEvents: "none",
  },

  royalTagPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(212, 175, 55, 0.15)",
    border: "1px solid rgba(212, 175, 55, 0.45)",
    color: "#fbd349",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "2px",
    textTransform: "uppercase",
    padding: "5px 14px",
    borderRadius: "30px",
    marginBottom: "14px",
    fontFamily: "system-ui, sans-serif",
  },

  royalTitle: {
    fontSize: "1.9rem",
    color: "#ffffff",
    fontWeight: "800",
    margin: "0 0 6px",
    lineHeight: "1.25",
  },

  royalTagline: {
    fontSize: "0.95rem",
    color: "#f3e5ab",
    margin: "0 0 18px",
    fontFamily: "system-ui, sans-serif",
  },

  royalDiscountBadge: {
    display: "inline-block",
    background: "linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%)",
    color: "#2e0f3c",
    padding: "9px 24px",
    borderRadius: "40px",
    fontSize: "1.45rem",
    fontWeight: "900",
    letterSpacing: "1px",
    marginBottom: "16px",
    border: "1px solid #ffe898",
    boxShadow: "0 6px 25px rgba(212, 175, 55, 0.4)",
  },

  royalScopeChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(0, 0, 0, 0.25)",
    border: "1px solid rgba(212, 175, 55, 0.3)",
    color: "#e2f1ec",
    fontSize: "12.5px",
    fontWeight: "600",
    padding: "5px 14px",
    borderRadius: "20px",
    marginBottom: "16px",
    fontFamily: "system-ui, sans-serif",
  },

  royalDesc: {
    color: "#f5edf7",
    fontSize: "14px",
    lineHeight: "1.6",
    margin: "0 auto 18px",
    maxWidth: "420px",
    fontFamily: "system-ui, sans-serif",
  },

  royalCouponStrip: {
    background: "rgba(255, 255, 255, 0.08)",
    border: "1.5px dashed rgba(212, 175, 55, 0.65)",
    borderRadius: "12px",
    padding: "10px 16px",
    marginBottom: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontFamily: "system-ui, sans-serif",
  },

  royalCopyBtn: {
    background: "#D4AF37",
    color: "#361445",
    padding: "7px 12px",
    borderRadius: "7px",
    fontSize: "11px",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  royalValidityNote: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    fontSize: "12px",
    color: "#f3e5ab",
    marginBottom: "20px",
    fontFamily: "system-ui, sans-serif",
  },

  royalCtaBtn: {
    background: "linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%)",
    color: "#361445",
    borderRadius: "35px",
    padding: "15px",
    fontSize: "15.5px",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    letterSpacing: "0.5px",
  },
};

export default AddCouponForm;